import { ArrowPathIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { useMemo, useState } from 'react';
import { ComboBox as AriaComboBox, Group, Input, Header } from 'react-aria-components';
import { EMPTY_ARRAY } from '../../../../shared/constants';
import { DropdownItem, DropdownSection, ListBox } from './dropdown';
import { Label } from './label';
import { Popover } from './popover';

const ALWAYS_TRUE = () => true;

export function Autocomplete({
  label,
  name,
  inputValue,
  onInputChange,
  items,
  children,
  loading,
  onSelect,
  ...props
}) {
  return (
    <AriaComboBox
      {...props}
      inputValue={inputValue}
      onSelectionChange={(...args) => {
        if (typeof onSelect === 'function') {
          try {
            onSelect(...args);
          } catch (error) {
            console.error('Bug on select handler', error);
          }
        }
      }}
      onInputChange={onInputChange}
      defaultFilter={ALWAYS_TRUE}
      className="group flex flex-col gap-1"
      allowsEmptyCollection
    >
      <Label>{label}</Label>
      <Group className="flex items-center relative justify-stretch w-full text-body-dark rounded-md border border-solid border-300 shadow-sm placeholder:text-light-500 focus-within:ring-1 ring-primary focus-within:border-primary">
        <MagnifyingGlassIcon
          className="absolute left-2 top-1/2 -translate-y-2.5 size-5 pointer-events-none text-light-400 grow-0"
          aria-hidden="true"
        />
        <Input
          {...props}
          name={name}
          className="py-2 pl-9 pr-2 border-0 focus:ring-0 focus:border-0 grow bg-transparent text-base"
        />
      </Group>
      <Popover
        className="w-[--trigger-width] group/popup"
        data-loading={!!loading}
        data-empty={items.length === 0}
      >
        <ListBox items={items}>{children}</ListBox>
        <Header className="px-2 py-1.5 hidden group-data-[loading=true]/popup:block">
          <ArrowPathIcon
            className="size-5 mr-2 pointer-events-none text-gray-300 grow-0 animate-spin"
            aria-hidden="true"
          />
        </Header>
        <Header className="text-base text-gray-500 italic truncate px-2 py-1.5 hidden group-data-[empty=true]/popup:group-data-[loading=false]/popup:block">
          No results found
        </Header>
      </Popover>
    </AriaComboBox>
  );
}

Autocomplete.Item = DropdownItem;
Autocomplete.Section = DropdownSection;

export function makeAutocompleteHook(useAsyncAbortableHook) {
  return () => {
    const [search, setSearch] = useState('');
    const searchState = useAsyncAbortableHook(search);
    const autocompleteProps = useMemo(
      () => ({
        inputValue: search,
        onInputChange: setSearch,
        loading: !!search && searchState.loading,
        items: searchState.result ?? EMPTY_ARRAY,
      }),
      [search, searchState.loading, searchState.result],
    );
    return {
      ...searchState,
      autocompleteProps,
    };
  };
}
